import { userLogin, userLogout } from '@/actions/authActions';
import { getClientSessionId, getHeaders } from '@/server/server';
import { LoginWindowState } from '@/state';
import { useDispatch, useSelector } from '@/state/store';
import { useState } from 'react';
import { SVVModal } from '@komponentkassen/svv-modal';
import { SVVTextInput } from '@komponentkassen/svv-form';
import { SVVButtonSubmit } from '@komponentkassen/svv-button';
import { stateLoginWindow } from '@/selectors/selectors';

interface LoginFormProps {
    handleWindowChange: (window: LoginWindowState) => void;
    isLoggedIn: boolean;
}

export const LoginForm = ({ handleWindowChange, isLoggedIn }: LoginFormProps) => {
    const dispatch = useDispatch();
    const config = useSelector(s => s.config);

    const [userVal, setUserVal] = useState('');
    const [passVal, setPassVal] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const loginState = useSelector(stateLoginWindow);

    const onLogin = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const headers = getHeaders(getClientSessionId());
        fetch(config.restapi_url + '/auth/login', {
            headers: { ...headers, 'Content-Type': 'application/json', Accept: 'application/json' },
            method: 'post',
            body: JSON.stringify({
                brukernavn: userVal,
                passord: passVal,
                brukertype: 'EKSTERN',
            }),
        })
            .then(response => {
                if (response.ok) return response.json();
                return Promise.reject(response);
            })
            .then(tokens => {
                handleWindowChange(LoginWindowState.NONE);
                dispatch(userLogin(tokens));
            })
            .catch(err => {
                if (err?.status === 401) {
                    setErrorMessage('Feil ved innlogging. Brukernavn eller passord er feil.');
                } else {
                    console.error(err);
                    setErrorMessage('Feil ved innlogging. Kontakt support.');
                }
            });
    };

    const onLogout = () => {
        handleWindowChange(LoginWindowState.NONE);
        dispatch(userLogout());
    };

    return (
        <>
            <SVVModal
                show={loginState === LoginWindowState.LOGIN}
                title="Tilgang til sensitive data"
                onClose={() => handleWindowChange(LoginWindowState.NONE)}
                className="c-login"
            >
                {!isLoggedIn ? (
                    <>
                        <div>
                            <p className="c-login__paragraph">
                                Sensitive data er tilgjengelig i Vegkart ved pålogging for brukere med et
                                dokumentert faglig behov. Innlogget bruker er ansvarlig for at sensitive data
                                ikke videreformidles eller misbrukes.
                            </p>
                            <p>Logg inn med 6-bokstav/8-tegn brukernavn fra Statens vegvesen.</p>
                            <form
                                className="c-login__form"
                                style={{ marginTop: 24, marginBottom: 24 }}
                                onSubmit={(e: React.FormEvent<HTMLFormElement>) => onLogin(e)}
                            >
                                <SVVTextInput
                                    id="userName"
                                    label="Brukernavn"
                                    value={userVal}
                                    onChange={e => {
                                        setUserVal(e.target.value);
                                    }}
                                    required={true}
                                    autoComplete="username"
                                />
                                <SVVTextInput
                                    id="password"
                                    label="Passord"
                                    value={passVal}
                                    onChange={e => {
                                        setPassVal(e.target.value);
                                    }}
                                    required={true}
                                    autoComplete="password"
                                    type="password"
                                />
                                {errorMessage && <p className="c-login__error">{errorMessage}</p>}
                                <SVVButtonSubmit>Logg inn</SVVButtonSubmit>
                            </form>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="c-login__paragraph">Du er allerede logget inn. Ønsker du å logge ut?</p>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                            <button
                                id="logoutButton"
                                type={'submit'}
                                onClick={onLogout}
                                className={'dark-button'}
                                style={{ marginTop: 0, width: 134, height: 46, padding: 0 }}
                            >
                                Logg ut
                            </button>
                        </div>
                    </>
                )}
                <div className="c-login__footer">
                    <hr />
                    <p>
                        Trenger du tilgang eller ønsker mer informasjon om sensitive data?{' '}
                        <a
                            href="https://nvdb.atlas.vegvesen.no/docs/data-i-nvdb/data-som-ikke-er-apent-publisert"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            Finn informasjon på nvdb.no.
                        </a>
                    </p>
                </div>
            </SVVModal>
        </>
    );
};
